import {
  Timestamp,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from "firebase/firestore";

import { db, isFirebaseConfigured } from "../lib/firebase";

export const NEWS_FEED_LIMIT = 120;

/** Official feed starts from August 2026 (Asia/Tokyo). Older docs are removed. */
export const NEWS_FEED_MIN_CREATED_AT = new Date("2026-08-01T00:00:00+09:00");

export const NEWS_FEED_BRAND_LABELS: Record<string, string> = {
  pokemon: "Pokémon",
  onepiece: "ONE PIECE",
  gundam: "Gundam",
  mtg: "Magic",
  dragonball: "Dragon Ball",
};

/** Display order for Official Updates brand filters. */
export const NEWS_FEED_BRAND_ORDER = [
  "pokemon",
  "onepiece",
  "gundam",
  "mtg",
  "dragonball",
] as const;

export type NewsFeedBrandKey = (typeof NEWS_FEED_BRAND_ORDER)[number];

export function isNewsFeedBrandKey(value: string | null | undefined): value is NewsFeedBrandKey {
  return (
    !!value &&
    (NEWS_FEED_BRAND_ORDER as readonly string[]).includes(value)
  );
}

/** Map URL brandSlug (e.g. one-piece) back to feed brand key (onepiece). */
export function newsFeedBrandKeyFromSlug(
  brandSlug: string | null | undefined,
): NewsFeedBrandKey | null {
  if (!brandSlug) return null;
  if (isNewsFeedBrandKey(brandSlug)) return brandSlug;
  const hit = Object.entries(NEWS_FEED_BRAND_SLUGS).find(
    ([, slug]) => slug === brandSlug,
  );
  return hit && isNewsFeedBrandKey(hit[0]) ? hit[0] : null;
}

export const NEWS_FEED_BRAND_SLUGS: Record<string, string> = {
  pokemon: "pokemon",
  onepiece: "one-piece",
  gundam: "gundam",
  mtg: "magic",
  dragonball: "dragon-ball",
};

export type NewsFeedItem = {
  id: string;
  sourceAccount: string;
  brand: string;
  brandSlug: string;
  itemSlug: string;
  pagePath: string;
  displayName: string;
  title: string;
  text: string;
  bodyText: string;
  detailHtml: string;
  imageUrls: string[];
  createdAt: Date | null;
  fetchedAt: Date | null;
};

export function newsItemPath(
  item: Pick<
    NewsFeedItem,
    "brand" | "brandSlug" | "itemSlug" | "pagePath" | "id"
  >,
): string {
  if (item.pagePath) return item.pagePath;
  const brandSlug =
    item.brandSlug ||
    NEWS_FEED_BRAND_SLUGS[item.brand] ||
    item.brand ||
    "official";
  const slug = item.itemSlug || item.id;
  return `/news/${brandSlug}/${slug}`;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function isHttpUrl(value: unknown): value is string {
  return typeof value === "string" && /^https?:\/\//.test(value);
}

function isCoverImage(url: string): boolean {
  return !/dummy|placeholder|img_noimage|news_noimg|no[_-]?image|\/ogp\.(png|jpe?g|webp)|\/renewal0?\d?\/images\/common\/|logo_fw|icon_serch|icon_menu|icon_x\.|icon_youtube|btn_degital|\/ico_/i.test(
    url,
  );
}

function firstContentImageFromHtml(html: string): string {
  const tags = String(html).match(/<img\b[^>]*>/gi) || [];
  for (const tag of tags) {
    const src = (tag.match(/\ssrc=["']([^"']+)["']/i) || [])[1];
    if (isHttpUrl(src) && isCoverImage(src)) return src;
  }
  return youtubeThumbFromHtml(html);
}

function youtubeThumbFromHtml(html: string): string {
  const match = String(html).match(
    /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i,
  );
  return match ? `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg` : "";
}

function parseImageUrls(data: Record<string, unknown>): string[] {
  const collected: string[] = [];
  if (isHttpUrl(data.imageUrl)) collected.push(data.imageUrl);
  if (Array.isArray(data.imageUrls)) {
    collected.push(...data.imageUrls.filter(isHttpUrl));
  }
  if (Array.isArray(data.imageUrlsJson)) {
    collected.push(...data.imageUrlsJson.filter(isHttpUrl));
  } else if (typeof data.imageUrlsJson === "string" && data.imageUrlsJson) {
    try {
      const parsed = JSON.parse(data.imageUrlsJson) as unknown;
      if (Array.isArray(parsed)) collected.push(...parsed.filter(isHttpUrl));
      else if (isHttpUrl(parsed)) collected.push(parsed);
    } catch {
      // ignore malformed JSON
    }
  }
  const unique = Array.from(new Set(collected.filter(isCoverImage)));
  if (unique.length > 0) return unique;
  const fromHtml = firstContentImageFromHtml(String(data.detailHtml ?? ""));
  return fromHtml ? [fromHtml] : [];
}

function mapItem(id: string, data: Record<string, unknown>): NewsFeedItem {
  const brand = String(data.brand ?? "");
  const brandSlug =
    String(data.brandSlug ?? "") || NEWS_FEED_BRAND_SLUGS[brand] || brand;
  const itemSlug = String(data.itemSlug ?? "") || id;
  const pagePath =
    String(data.pagePath ?? "") || `/news/${brandSlug}/${itemSlug}`;
  return {
    id,
    sourceAccount: String(data.sourceAccount ?? ""),
    brand,
    brandSlug,
    itemSlug,
    pagePath,
    displayName: String(data.displayName ?? data.sourceAccount ?? "Official"),
    title: String(data.title ?? ""),
    text: String(data.text ?? ""),
    bodyText: String(data.bodyText ?? data.text ?? data.title ?? ""),
    detailHtml: String(data.detailHtml ?? ""),
    imageUrls: parseImageUrls(data),
    createdAt: toDate(data.createdAt),
    fetchedAt: toDate(data.fetchedAt),
  };
}

function isCurrentFeedItem(item: NewsFeedItem): boolean {
  if (!item.createdAt) return true;
  return item.createdAt.getTime() >= NEWS_FEED_MIN_CREATED_AT.getTime();
}

export function subscribeNewsFeed(
  onChange: (items: NewsFeedItem[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!isFirebaseConfigured || !db) {
    onChange([]);
    return () => undefined;
  }

  const q = query(
    collection(db, "newsItems"),
    orderBy("createdAt", "desc"),
    limit(NEWS_FEED_LIMIT),
  );

  return onSnapshot(
    q,
    (snap) => {
      onChange(
        snap.docs
          .map((docSnap) =>
            mapItem(docSnap.id, docSnap.data() as Record<string, unknown>),
          )
          .filter(isCurrentFeedItem),
      );
    },
    (error) => {
      onError?.(error);
    },
  );
}

export function subscribeNewsItem(
  brandSlug: string,
  itemSlug: string,
  onChange: (item: NewsFeedItem | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!isFirebaseConfigured || !db) {
    onChange(null);
    return () => undefined;
  }

  const firestore = db;
  const pagePath = `/news/${brandSlug}/${itemSlug}`;
  const q = query(
    collection(firestore, "newsItems"),
    where("pagePath", "==", pagePath),
    limit(1),
  );

  const unsub = onSnapshot(
    q,
    async (snap) => {
      const hit = snap.docs[0];
      if (hit) {
        onChange(mapItem(hit.id, hit.data() as Record<string, unknown>));
        return;
      }
      try {
        const byId = await getDoc(doc(firestore, "newsItems", itemSlug));
        if (byId.exists()) {
          onChange(mapItem(byId.id, byId.data() as Record<string, unknown>));
          return;
        }
      } catch {
        // ignore and report empty
      }
      onChange(null);
    },
    (error) => {
      onError?.(error);
    },
  );

  return unsub;
}
